import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { gettext } from '@eb/i18n-ebspa';
import classNames from 'classnames';
import { MediaCardContent } from '@eb/eds-media-card-content';
import { Card } from '@eb/eds-card';
import {
    IMAGE_STYLE_FIXED,
    STYLES_PROP_TYPE,
    IMAGE_STYLES_PROP_TYPE,
} from '@eb/eds-media-card-content';

import { getRandomFallbackImage } from './experimental/eventCard/utils';
import { parseDate } from '@eb/datetime';
import { GA_VIEW_ARTICLE_ACTION, GA_CITY_BROWSE_CATEGORY } from '../constants';

import './articleCard.scss';

const LIST_TYPE = 'list';
const MINI_STYLE = 'mini';

const _formatByLine = (byline) => (
    <span className="eds-text-weight--heavy">{byline}</span>
);
const _formatDate = (rawDate) => {
    const { day, month } = parseDate(rawDate);

    return gettext('%(month)s %(day)s', { month, day });
};

export const AuthorAndDate = ({ author, date }) => (
    <span>
        {author} • {date}
    </span>
);

const SubContentOne = ({ author, date, summary }) => {
    let component = <AuthorAndDate author={author} date={date} />;

    if (summary) {
        component = <span>{summary}</span>;
    }

    return component;
};

const SubContentTwo = ({ author, date, summary }) => {
    let component = null;

    if (summary) {
        component = (
            <div className="eds-l-pad-top-3">
                <AuthorAndDate author={author} date={date} />
            </div>
        );
    }

    return component;
};

export default class ArticleCard extends PureComponent {
    static propTypes = {
        /*
         * Article title
         */
        name: PropTypes.string.isRequired,
        /*
         * Link to the article content
         */
        url: PropTypes.string.isRequired,
        /*
         * Article Image
         */
        imageUrl: PropTypes.string,
        /*
         * Edge color of the image
         */
        edgeColor: PropTypes.string,
        /*
         * Author of the article
         */
        byline: PropTypes.string,
        /*
         * Style of MediaCardContent to be used
         */
        style: STYLES_PROP_TYPE,
        /*
         * Style of image to be used in the MediaCardContent
         */
        imageStyle: IMAGE_STYLES_PROP_TYPE,
        /*
         * Summary of the article
         */
        summary: PropTypes.string,
        /*
         * Date the article was published
         */
        published: PropTypes.string,
        /*
         * Overrides the default click handler
         */
        onClick: PropTypes.func,
        /*
         * Track GA Events from state
         */
        trackGAEventOnPageUnload: PropTypes.func,
    };

    static defaultProps = {
        style: MINI_STYLE,
        // eslint-disable-next-line react/default-props-match-prop-types
        type: LIST_TYPE,
        imageStyle: IMAGE_STYLE_FIXED,
    };

    _handleArticleClick = () => {
        const { name, onClick, trackGAEventOnPageUnload } = this.props;

        trackGAEventOnPageUnload({
            category: GA_CITY_BROWSE_CATEGORY,
            action: GA_VIEW_ARTICLE_ACTION,
            label: name,
        });

        if (onClick) {
            onClick();
        }
    };

    render() {
        const {
            imageUrl,
            edgeColor,
            name,
            url,
            byline,
            style,
            imageStyle,
            summary,
            published,
        } = this.props;

        const classes = 'eds-l-pad-hor-3 eds-l-pad-vert-2';
        const cardClasses = classNames('eds-g-cell', 'eds-g-cell-1-1');

        let contentOne;

        let contentTwo;

        if (byline && published) {
            const formattedAuthor = _formatByLine(byline);
            const formattedDate = _formatDate(published);

            contentOne = (
                <SubContentOne
                    author={formattedAuthor}
                    date={formattedDate}
                    summary={summary}
                />
            );

            contentTwo = (
                <SubContentTwo
                    author={formattedAuthor}
                    date={formattedDate}
                    summary={summary}
                />
            );
        }

        const truncatedTitle = (
            <div className="card-text--truncated__three">{name}</div>
        );

        const articleImage = imageUrl || getRandomFallbackImage();

        return (
            <div className={classes}>
                <Card style="neutral">
                    <div className={cardClasses}>
                        <MediaCardContent
                            style={style}
                            type={LIST_TYPE}
                            imageStyle={imageStyle}
                            imageUrl={articleImage}
                            placeholderBgColor={edgeColor}
                            linkUrl={url}
                            onClick={this._handleArticleClick}
                            title={truncatedTitle}
                            shouldShowDivider={true}
                            subContentOne={contentOne}
                            subContentTwo={contentTwo}
                        />
                    </div>
                </Card>
            </div>
        );
    }
}
